// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import { formatHumanName, getReferenceString } from '@medplum/core';
import type { Appointment, CareTeam, Patient, Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { POLARIS_PROVIDER_ROLE_SYSTEM } from '@polarissleep/scheduling';
import { useCallback, useEffect, useState } from 'react';

export interface CareTeamMember {
  practitioner: Practitioner;
  name: string;
  role: string;
  lastInteraction: string | undefined;
  nextAppointment: Appointment | undefined;
}

async function resolveMember(
  medplum: MedplumClient,
  practitioner: Practitioner,
  patientRef: string
): Promise<CareTeamMember> {
  const practitionerRef = getReferenceString(practitioner);

  const [roleResource, appointments] = (await Promise.all([
    medplum.searchOne('PractitionerRole', { practitioner: practitionerRef }).catch(() => undefined),
    medplum
      .searchResources('Appointment', {
        patient: patientRef,
        actor: practitionerRef,
        _sort: '-date',
        _count: '50',
      })
      .catch(() => [] as Appointment[]),
  ])) as [PractitionerRole | undefined, Appointment[]];

  const roleCoding = roleResource?.code?.[0]?.coding?.find((c) => c.system === POLARIS_PROVIDER_ROLE_SYSTEM);
  const role = roleCoding?.display ?? roleCoding?.code ?? '—';

  const now = Date.now();
  const apptArray = Array.from(appointments);
  const completed = apptArray
    .filter((a) => a.status === 'fulfilled' && a.start && new Date(a.start).getTime() < now)
    .sort((a, b) => new Date(b.start ?? 0).getTime() - new Date(a.start ?? 0).getTime());
  const upcoming = apptArray
    .filter((a) => a.start && new Date(a.start).getTime() >= now && a.status !== 'cancelled')
    .sort((a, b) => new Date(a.start ?? 0).getTime() - new Date(b.start ?? 0).getTime());

  return {
    practitioner,
    name: practitioner.name?.[0] ? formatHumanName(practitioner.name[0]) : 'Unnamed provider',
    role,
    lastInteraction: completed[0]?.start,
    nextAppointment: upcoming[0],
  };
}

export interface UseCareTeamMembersResult {
  careTeam: CareTeam | undefined;
  members: CareTeamMember[];
  loading: boolean;
  error: Error | null;
  setCareTeam: (careTeam: CareTeam | undefined) => void;
  refresh: () => void;
}

export function useCareTeamMembers(patient: Patient | undefined): UseCareTeamMembersResult {
  const medplum = useMedplum();
  const [careTeam, setCareTeam] = useState<CareTeam | undefined>();
  const [members, setMembers] = useState<CareTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const patientRef = `Patient/${patient.id}`;
      const team = (await medplum.searchOne('CareTeam', {
        subject: patientRef,
        _sort: '-_lastUpdated',
      })) as CareTeam | undefined;
      setCareTeam(team as CareTeam | undefined);

      const participants = ((team as CareTeam | undefined)?.participant ?? [])
        .map((p) => p.member?.reference)
        .filter((ref): ref is string => Boolean(ref?.startsWith('Practitioner/')));

      const practitioners = await Promise.all(
        participants.map(
          (ref): Promise<Practitioner | undefined> =>
            medplum.readReference<Practitioner>({ reference: ref }).catch(() => undefined)
        )
      );
      const validPractitioners = practitioners.filter((p: Practitioner | undefined): p is Practitioner => Boolean(p));

      const resolved = await Promise.all(validPractitioners.map((p) => resolveMember(medplum, p, patientRef)));
      setMembers(resolved);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load care team'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { careTeam, members, loading, error, setCareTeam, refresh: fetch };
}
